import { useState, useEffect } from "react";
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import theme from "../../../assets/theme";
import { Form, Formik, FormikProps } from "formik";
import { Grid } from "@mui/material";
import FormField from "../../../components/FormField";
import FaiButton from "../../../MuiComponents/FaiButton";
import { merchantAuthController } from "../../../controllers/authController";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../../context/ProfileContext";

interface InitialObject {
	firstName: string;
	lastName: string;
	email: string;
	designation: string;
	contactnumber: string;
	isDraft: boolean;
}

function ContactInformation() {
	const { typography, palette, borders } = theme;
	const { profile, setProfile } = useProfile();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);
	const [initialObject, setInitialObject] = useState<InitialObject>({
		firstName: profile?.contactInformation?.firstName || "",
		lastName: profile?.contactInformation?.lastName || "",
		email: profile?.contactInformation?.email || "",
		designation: profile?.contactInformation?.designation || "",
		contactnumber: profile?.contactInformation?.primaryContactNo || "",
		isDraft: false,
	});
	const navigate = useNavigate();
	// OTP verification states
	const [otpValue, setOtpValue] = useState("");
	const [requestId, setRequestId] = useState("");
	const [phoneVerified, setPhoneVerified] = useState(false);
	const [phoneVerificationLoading, setPhoneVerificationLoading] =
		useState(false);
	const [phoneVerificationError, setPhoneVerificationError] = useState<
		string | null
	>(null);
	const [phoneVerificationSuccess, setPhoneVerificationSuccess] =
		useState(false);
	const [showOtpSection, setShowOtpSection] = useState(false);
	const [phoneNumber, setPhoneNumber] = useState("");

	// Update form values when profile is loaded/changed
	useEffect(() => {
		if (profile?.contactInformation) {
			setInitialObject({
				firstName: profile.contactInformation.firstName || "",
				lastName: profile.contactInformation.lastName || "",
				email: profile.contactInformation.email || "",
				designation: profile.contactInformation.designation || "",
				contactnumber: profile.contactInformation.primaryContactNo || "",
				isDraft: false,
			});
		}
	}, [profile]);

	const handleSubmit = async (
		values: InitialObject,
		{ setSubmitting }: any
	) => {
		try {
			setLoading(true);
			setError(null);

			// Transform the form values to match the API requirements
			const apiData = {
				firstName: values.firstName,
				lastName: values.lastName,
				email: values.email,
				isDraft: values.isDraft,
				designation: values.designation,
				primaryContactNo: values.contactnumber,
			};

			// Save contact information
			const response = await merchantAuthController.setContactInfo(apiData);

			// Update the profile context with the new contact information
			setProfile({
				...profile,
				contactInformation: {
					firstName: values.firstName,
					lastName: values.lastName,
					email: values.email,
					designation: values.designation,
					primaryContactNo: values.contactnumber,
				},
			});

			if (response?.data?.data?.skipSMSVerification)
				return toast.success("Contact information saved successfully!");

			// Check if response has data and requestId
			if (response.data?.data?.requestId) {
				const requestId = response.data.data.requestId;
				setRequestId(requestId);
				setSuccess(true);
				setPhoneNumber(values.contactnumber);
				setShowOtpSection(true);
				toast.success(
					"Contact information saved successfully! Please verify your phone number."
				);

				// After saving contact info, send SMS OTP
				try {
					const otpResponse = await merchantAuthController.resendSmsOtp({
						mobile: values.contactnumber,
					});

					if (otpResponse.data?.data?.requestId) {
						setRequestId(otpResponse.data.data.requestId);
						toast.info("OTP has been sent to your phone number");
					}
				} catch (otpError: any) {
					console.error("Failed to send OTP:", otpError);
				}
			} else {
				throw new Error("Invalid response format: Missing requestId");
			}
		} catch (err: any) {
			console.error("Contact info error:", err);
			const errorMessage =
				err.response?.data?.message ||
				err.message ||
				"Failed to update contact information";
			setError(errorMessage);
			toast.error(errorMessage);
		} finally {
			setLoading(false);
			setSubmitting(false);
		}
	};

	const handleVerifyOtp = async () => {
		if (!requestId || !otpValue) {
			toast.error("Please enter the OTP");
			return;
		}

		try {
			setPhoneVerificationLoading(true);
			setPhoneVerificationError(null);

			const verifyResponse = await merchantAuthController.verifySmsOtp({
				requestId: requestId,
				otp: otpValue,
			});

			// Check if the response indicates success (status 200)
			if (verifyResponse.status === 200) {
				setPhoneVerificationSuccess(true);
				setPhoneVerified(true);
				toast.success("Phone number verified successfully!");
			} else {
				throw new Error("Verification failed");
			}
		} catch (err: any) {
			console.error("OTP verification error:", err);
			const errorMessage = "Failed to verify OTP";
			setPhoneVerificationError(errorMessage);
			toast.error(errorMessage);
		} finally {
			setPhoneVerificationLoading(false);
			navigate("/app/onboarding/businessdetails");
		}
	};

	const handleResendOtp = async () => {
		try {
			setPhoneVerificationLoading(true);
			setPhoneVerificationError(null);

			const response = await merchantAuthController.resendSmsOtp({
				mobile: phoneNumber,
			});

			if (response.data?.requestId) {
				setRequestId(response.data.requestId);
				setPhoneVerificationError(null);
				toast.info("New OTP has been sent to your phone number");
			}
		} catch (err: any) {
			console.error("Resend OTP error:", err);
			const errorMessage =
				err.response?.data?.message || "Failed to resend OTP";
			setPhoneVerificationError(errorMessage);
			toast.error(errorMessage);
		} finally {
			setPhoneVerificationLoading(false);
		}
	};

	const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setOtpValue(e.target.value);
	};

	return (
		<>
			<FaiBox>
				<FaiBox
					bgColor={palette.white.main}
					className={"coupon-form-content"}
					mt={"15px"}
					p={"15px"}
					borderRadius={borders.borderRadius.md}
				>
					<FaiBox>
						<FaiTypography
							variant="h4"
							fontWeight={typography.fontWeightMedium}
						>
							Contact Information
						</FaiTypography>
						<FaiTypography variant="h6" mb={"21px"}>
							Please provide the details of the primary contact person for your
							company. This person will be the point of contact between your
							company and Styola.
						</FaiTypography>
					</FaiBox>

					<Formik
						initialValues={initialObject}
						enableReinitialize={true}
						validateOnBlur={true}
						validateOnChange={true}
						onSubmit={handleSubmit}
					>
						{(props: FormikProps<InitialObject>) => {
							const {
								handleSubmit,
								setFieldValue,
								handleChange,
								handleBlur,
								values,
								touched,
								isValid,
								errors,
							} = props;
							return (
								<Form>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"First Name"}
													name={"firstName"}
													placeholder={"Enter First Name"}
													value={values.firstName}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.firstName && Boolean(errors.firstName)}
													helperText={touched.firstName && errors.firstName}
												/>
											</FaiBox>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Last Name"}
													name={"lastName"}
													placeholder={"Enter Last Name"}
													value={values.lastName}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.lastName && Boolean(errors.lastName)}
													helperText={touched.lastName && errors.lastName}
												/>
											</FaiBox>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Email Address"}
													name={"email"}
													placeholder={"Enter Email"}
													value={values.email}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.email && Boolean(errors.email)}
													helperText={touched.email && errors.email}
												/>
											</FaiBox>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Primary Contact No."}
													name={"contactnumber"}
													placeholder={"Enter Number"}
													value={values.contactnumber}
													onChange={handleChange}
													onBlur={handleBlur}
													error={
														touched.contactnumber &&
														Boolean(errors.contactnumber)
													}
													helperText={
														touched.contactnumber && errors.contactnumber
													}
												/>
											</FaiBox>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Designation"}
													name={"designation"}
													placeholder={"Enter Designation"}
													value={values.designation}
													onChange={handleChange}
													onBlur={handleBlur}
													error={
														touched.designation && Boolean(errors.designation)
													}
													helperText={touched.designation && errors.designation}
												/>
											</FaiBox>
										</Grid>
									</Grid>

									{error && (
										<FaiTypography color="error" mt={2}>
											{error}
										</FaiTypography>
									)}

									{success && (
										<FaiTypography color="success" mt={2}>
											Contact information updated successfully!
										</FaiTypography>
									)}

									<FaiBox pt={"15px"}>
										<Grid container justifyContent="flex-end">
											<Grid item sm={1} xs={12}>
												<FaiButton
													color="primary"
													variant={"contained"}
													fullWidth
													type="submit"
													disabled={loading}
												>
													{loading ? "Saving..." : "Submit"}
												</FaiButton>
											</Grid>
										</Grid>
									</FaiBox>
								</Form>
							);
						}}
					</Formik>
				</FaiBox>
			</FaiBox>

			{showOtpSection && (
				<FaiBox
					bgColor={palette.white.main}
					className={"coupon-form-content"}
					mt={"15px"}
					p={"15px"}
					borderRadius={borders.borderRadius.md}
				>
					<FaiBox>
						<FaiTypography
							variant="h4"
							fontWeight={typography.fontWeightMedium}
						>
							Verify your Phone no.
						</FaiTypography>
						<FaiTypography variant="h6" mb={"21px"}>
							Please enter the One Time Password (OTP) that has been sent to
							your {phoneNumber}.
						</FaiTypography>
					</FaiBox>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<FaiBox xs={12} sm={6}>
								<FormField
									label={"Enter OTP"}
									name={"otp"}
									placeholder={"Enter OTP"}
									value={otpValue}
									onChange={handleOtpChange}
									disabled={phoneVerified}
								/>
							</FaiBox>
						</Grid>
					</Grid>

					{phoneVerificationError && (
						<FaiTypography color="error" mt={2}>
							{phoneVerificationError}
						</FaiTypography>
					)}

					{phoneVerificationSuccess && (
						<FaiTypography color="success" mt={2}>
							Phone number verified successfully!
						</FaiTypography>
					)}

					<FaiBox pt={"15px"}>
						<Grid container justifyContent="flex-end" spacing={2}>
							<Grid item sm={2} xs={12}>
								<FaiButton
									variant={"contained"}
									fullWidth
									onClick={handleResendOtp}
									disabled={phoneVerificationLoading || phoneVerified}
								>
									Resend OTP
								</FaiButton>
							</Grid>
							<Grid item sm={2} xs={12}>
								<FaiButton
									color="primary"
									variant={"contained"}
									fullWidth
									onClick={handleVerifyOtp}
									disabled={
										phoneVerificationLoading || !otpValue || phoneVerified
									}
								>
									{phoneVerificationLoading ? "Verifying..." : "Verify OTP"}
								</FaiButton>
							</Grid>
						</Grid>
					</FaiBox>
				</FaiBox>
			)}
		</>
	);
}
export default ContactInformation;